<template>
  <b-modal
    id="modal-authentication"
    :title="$t('Autenticação Requerida')"
    header-class="py-1 px-2"
    body-class="p-1"
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    hide-footer
    centered
    size="sm"
    @show="onBeforeShow"
  >
    <div class="auth-wrapper auth-v1 px-1">
      <div class="auth-inner py-1">
        <b-card class="mb-0">
          <b-link class="brand-logo mt-2 mb-2">
            <img
              :src="style.loginImage"
              width="150px"
            >
          </b-link>

          <b-card-text class="mb-1">
            {{ instructionMessage }}
          </b-card-text>

          <validation-observer
            ref="loginForm"
            v-slot="{ invalid }"
          >
            <b-form
              class="auth-login-form mt-1"
              @submit.prevent
            >
              <!-- email -->
              <b-form-group
                label-for="email"
                :label="$t('Usuário')"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="$t('Usuário')"
                  rules="required"
                >
                  <b-form-input
                    id="email"
                    v-model="userName"
                    v-keyboard="{ layout: 'default' }"
                    name="username"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t('Usuário')"
                    autofocus
                    tabindex="1"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- password -->
              <b-form-group>
                <validation-provider
                  v-slot="{ errors }"
                  :name="$t('Senha')"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="password"
                      v-model="password"
                      v-keyboard="{ layout: 'default' }"
                      :type="passwordFieldType"
                      class="form-control-merge"
                      :state="errors.length > 0 ? false : null"
                      name="password"
                      :placeholder="$t('Senha')"
                      tabindex="2"
                      data-private
                    />

                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- submit button -->
              <e-button
                data-test="LoginSubmitButton"
                type="submit"
                variant="primary"
                class="mr-1"
                block
                :text="$t('Entrar')"
                :busy="busy"
                :disabled="invalid"
                @click="login"
              />
              <label
                v-show="capsLockState"
                class="text-danger"
              >A tecla CapsLock está ligada!</label>
            </b-form>
          </validation-observer>
        </b-card>
      </div>
    </div>
  </b-modal>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import jwt from 'jsonwebtoken'
import { EButton } from '@/views/components'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BForm,
  BFormInput,
  BFormGroup,
  BCard,
  BLink,
  BCardText,
  BInputGroup,
  BInputGroupAppend,
} from 'bootstrap-vue'
import { acl, app } from '@/mixins'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@/@core/mixins/ui/forms'
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  components: {
    EButton,
    ValidationProvider,
    ValidationObserver,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BLink,
    BCardText,
    BInputGroup,
    BInputGroupAppend
  },
  mixins: [togglePasswordVisibility, acl, app],
  data() {
    return {
      userName: '',
      password: '',
      status: '',
      operator: '',
      hubs: [],
      required,
      email,
      busy: false,
    }
  },
  computed: {
    ...mapState('app/keyboard', ['capsLockState']),
    ...mapGetters('pages/pdv/payBoxConfiguration', ['isPdvAtm']),
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    sessionExpired() {
      return this.$store.state.auth.sessionExpired
    },
    showReauthModal() {
      return this.$store.state.auth.showReauthModal
    },
    instructionMessage() {
      return this.isPdvAtm
        ? this.$t('Acesso expirado. Por favor, contate um atendente.')
        : this.$t('Por favor, faça o login na sua conta.')
    }
  },
  watch: {
    showReauthModal(newValue) {
      if (newValue) {
        this.openAuthModal()
        return
      }

      this.$bvModal.hide('modal-authentication')
    },
    $route() {
      if (this.isAuthRoute()) {
        this.closeAuthModalState()
      }
    },
  },
  mounted() {
    if (this.isAuthRoute()) {
      this.closeAuthModalState()
      return
    }

    if (this.sessionExpired && this.showReauthModal) {
      this.openAuthModal()
    }
  },
  methods: {
    ...mapActions('app', ['fetchCompanies']),
    closeAuthModalState() {
      this.$store.dispatch('auth/sessionRecovered')
      this.$bvModal.hide('modal-authentication')
    },
    openAuthModal() {
      if (!this.sessionExpired) {
        return
      }

      if (this.isAuthRoute()) {
        this.closeAuthModalState()
        return
      }

      this.$bvModal.show('modal-authentication')
    },
    onBeforeShow(evt) {
      if (this.isAuthRoute() || !this.sessionExpired || !this.showReauthModal) {
        evt.preventDefault()
      }
    },
    isAuthRoute() {
      const authRouteNames = [
        'auth-login',
        'auth-forgot-password',
        'auth-reset-password',
        'primeiro-acesso',
        'pdv-pay-box-inactive',
        'pdv-pay-box-unknown-error',
      ]
      return authRouteNames.includes(this.$route?.name)
    },
    async login() {
      if (await this.$refs.loginForm.validate()) {
        this.busy = true
        useJwt
          .login({
            userName: this.userName,
            password: this.password,
          })
          .then(response => {
            const {
              email: userEmail,
              userName,
              token,
              permissions,
            } = response.data

            const userDecoded = jwt.decode(token)

            const userData = {
              email: userEmail,
              userName,
              fullName: userName,
              userId: userDecoded.nameid,
            }

            useJwt.setToken(token)
            useJwt.setRefreshToken(null)
            this.$store.dispatch('auth/setUser', userData)
            this.$store.dispatch('auth/setPermissions', permissions)
            this.$store.dispatch('app/fetchCombos')
            this.configureAbility()
            this.fetchCompanies()
            this.onHideModal()
          })
          .catch(error => {
            console.log('Login error:', error)
            this.showError({
              title: this.$t('Autenticação'),
              error,
            })
          })
          .finally(() => {
            this.busy = false
          })
      }
    },
    onHideModal() {
      this.closeAuthModalState()
    },
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

#modal-authentication {
  .auth-wrapper {
    min-height: auto !important;
    height: auto !important;
    padding: 0 !important;
  }

  .auth-v1 {
    display: block !important;
  }

  .modal-dialog {
    margin-top: 0;
    margin-bottom: 0;
    max-height: 100vh;
  }

  .modal-content {
    max-height: 100vh;
    overflow: hidden;
  }

  .modal-body {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .auth-inner {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .brand-logo {
    margin-top: 0.5rem !important;
    margin-bottom: 0.75rem !important;
  }

  .auth-login-form {
    margin-top: 0.75rem !important;
  }

  .card {
    margin-bottom: 0 !important;
  }

  .card-body {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
}
</style>